import { useEffect, useState } from 'react';
import type { FC } from 'react';

import branch from 'branch-sdk';
import type { JourneyEvent } from 'branch-sdk';
import type { RouteComponentProps } from 'react-router';

import type { PropsFromRedux } from './container';
import { getDeepLinkPathFromPathAndParams } from './utils';

export const SmartBannerRenderer: FC<PropsFromRedux & RouteComponentProps> = ({
  accountId,
  dispatchCurrentlyLoggedInPatientHealthkitRecordsAvailable,
  dispatchObtainMagicTokenAction,
  dispatchClickCloseSmartBanner,
  dispatchClickGetSmartBanner,
  dispatchCloseSmartBanner,
  dispatchShowSmartBanner,
  isInOrderTracking,
  pathName,
  params,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Order tracking will not be a part of the experiment so we always show
  // the banner on that page
  const showBranchBanner = accountId !== null;

  const initializeBranch = async () => {
    if (!isSubmitting && process.env.NEXT_PUBLIC_BRANCH_KEY) {
      setIsSubmitting(true);
      // The below api calls are only used in order tracking
      const recordsAvilable = isInOrderTracking
        ? await dispatchCurrentlyLoggedInPatientHealthkitRecordsAvailable()
        : null;
      const response = isInOrderTracking ? await dispatchObtainMagicTokenAction() : null;
      const magicToken = response?.data?.data?.attributes?.temp_magic_token;
      const deepLinkPath = getDeepLinkPathFromPathAndParams(pathName, params);

      const bannerData = {
        data: {
          healthkit_records_available: recordsAvilable,
          magic_token: magicToken,
          account_id: accountId,
          $deeplink_path: deepLinkPath,
        },
      };
      branch.init(process.env.NEXT_PUBLIC_BRANCH_KEY);
      branch.setBranchViewData(bannerData);

      const listener = function (event: JourneyEvent) {
        if (event === 'didShowJourney') {
          dispatchShowSmartBanner(accountId, pathName);
        } else if (event === 'didClickJourneyCTA') {
          dispatchClickGetSmartBanner(accountId, pathName);
        } else if (event === 'didClickJourneyClose') {
          dispatchClickCloseSmartBanner(accountId, pathName);
        } else if (event === 'didCloseJourney') {
          dispatchCloseSmartBanner(accountId, pathName);
        }
      };
      branch.addListener(undefined, listener);

      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (showBranchBanner) {
      initializeBranch();
    }
  }, [accountId, pathName, showBranchBanner]); // eslint-disable-line

  useEffect(() => {
    return () => {
      branch.closeJourney();
    };
  }, []);
  return null;
};
